import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from '../../components/ui/use-toast';
import { apiRequest } from '../../config/api';
import { 
  Shield, 
  Building2, 
  Search, 
  CheckCircle2, 
  XCircle, 
  AlertCircle, 
  Edit, 
  Trash2,
  LogOut,
  Filter,
  RefreshCw,
  Clock,
  Calendar,
  Users,
  Mail,
  Phone,
  MapPin,
  User,
  Lock,
  Eye,
  EyeOff,
  Image,
  Upload,
  X,
  Globe,
  ExternalLink,
  FileText,
  Printer,
  Send
} from 'lucide-react';
import { pdf } from '@react-pdf/renderer';
import InvoicePDF from '../../components/pdf/InvoicePDF';

const DentalHubAdminDashboard = () => {
  const [hospitals, setHospitals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [adminUser, setAdminUser] = useState(null);
  const [selectedHospital, setSelectedHospital] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editFormData, setEditFormData] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [existingLogo, setExistingLogo] = useState(null);
  const [originalLogo, setOriginalLogo] = useState(null); // Track original logo URL
  const [enrollmentRequests, setEnrollmentRequests] = useState([]);
  const [approvedEnrollmentRequests, setApprovedEnrollmentRequests] = useState([]);
  const [loadingEnrollments, setLoadingEnrollments] = useState(false);
  const [enrollmentFilter, setEnrollmentFilter] = useState('all'); // 'all', 'pending', 'approved'
  const [activeTab, setActiveTab] = useState('hospitals'); // 'hospitals' or 'enrollments'
  const [showEditPlanModal, setShowEditPlanModal] = useState(false);
  const [selectedPlanHospital, setSelectedPlanHospital] = useState(null);
  const [planFormData, setPlanFormData] = useState({
    plan_type: 'TRIAL',
    billing_period: 'MONTHLY',
    start_date: '',
    end_date: ''
  });
  const navigate = useNavigate();

  useEffect(() => {
    // Check if admin is logged in
    const adminSession = localStorage.getItem('admin_session_token');
    const adminData = localStorage.getItem('admin_user');
    
    if (!adminSession || !adminData) {
      navigate('/admin/hub/login');
      return;
    }
    
    setAdminUser(JSON.parse(adminData));
    fetchHospitals();
    fetchEnrollmentRequests();
  }, [navigate]);

  const fetchHospitals = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterStatus !== 'all') {
        params.append('status', filterStatus);
      }
      if (searchQuery) {
        params.append('search', searchQuery);
      }
      
      const response = await apiRequest(`admin_get_hospitals.php?${params.toString()}`);
      
      if (response.success) {
        setHospitals(response.hospitals || []);
      } else {
        throw new Error(response.error || 'Failed to fetch hospitals');
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to load hospitals',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchEnrollmentRequests = async () => {
    setLoadingEnrollments(true);
    try {
      // Fetch all enrollment requests
      const response = await apiRequest('get_enrollment_requests.php');
      
      if (response.success) {
        const allRequests = response.requests || [];
        // Separate pending and approved requests
        setEnrollmentRequests(allRequests.filter(req => req.status === 'PENDING'));
        setApprovedEnrollmentRequests(allRequests.filter(req => req.status === 'APPROVED'));
      } else {
        throw new Error(response.error || 'Failed to fetch enrollment requests');
      }
    } catch (error) {
      console.error('Failed to fetch enrollment requests:', error);
      setEnrollmentRequests([]);
      setApprovedEnrollmentRequests([]);
    } finally {
      setLoadingEnrollments(false);
    }
  };

  const handleApproveEnrollment = async (requestId) => {
    try {
      const adminData = JSON.parse(localStorage.getItem('admin_user'));
      const response = await apiRequest('approve_enrollment_request.php', {
        method: 'POST',
        body: JSON.stringify({
          request_id: requestId,
          admin_id: adminData.id
        })
      });

      if (response.success) {
        toast({
          title: 'Success',
          description: 'Enrollment request approved successfully. Subscription updated.',
        });
        fetchEnrollmentRequests();
        fetchHospitals(); // Refresh hospitals to show updated plan
      } else {
        throw new Error(response.error || 'Failed to approve enrollment request');
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to approve enrollment request',
        variant: 'destructive'
      });
    }
  };

  const handleGenerateInvoice = async (request) => {
    try {
      const response = await apiRequest('generate_invoice.php', {
        method: 'POST',
        body: JSON.stringify({
          enrollment_request_id: request.id
        })
      });

      if (response.success) {
        return response.invoice;
      } else {
        throw new Error(response.error || 'Failed to generate invoice');
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to generate invoice',
        variant: 'destructive'
      });
      return null;
    }
  };

  const handleViewInvoice = async (request) => {
    try {
      const invoiceData = await handleGenerateInvoice(request);
      if (!invoiceData) return;

      // Generate PDF
      const doc = <InvoicePDF invoiceData={invoiceData} />;
      const pdfBlob = await pdf(doc).toBlob();
      const url = URL.createObjectURL(pdfBlob);
      
      // Open in new window
      window.open(url, '_blank');
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to view invoice',
        variant: 'destructive'
      });
    }
  };

  const handlePrintInvoice = async (request) => {
    try {
      const invoiceData = await handleGenerateInvoice(request);
      if (!invoiceData) return;

      // Generate PDF
      const doc = <InvoicePDF invoiceData={invoiceData} />;
      const pdfBlob = await pdf(doc).toBlob();
      const url = URL.createObjectURL(pdfBlob);
      
      // Create iframe for printing
      const iframe = document.createElement('iframe');
      iframe.style.display = 'none';
      document.body.appendChild(iframe);
      iframe.src = url;
      iframe.onload = () => {
        iframe.contentWindow.print();
        setTimeout(() => {
          document.body.removeChild(iframe);
          URL.revokeObjectURL(url);
        }, 1000);
      };
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to print invoice',
        variant: 'destructive'
      });
    }
  };

  const handleEditPlan = (hospital) => {
    setSelectedPlanHospital(hospital);
    
    // Determine current plan type
    const currentPlanType = hospital.plan_type || 'TRIAL';
    const currentPlan = hospital.current_plan || null;
    
    // Set default dates
    const today = new Date();
    const startDate = hospital.subscription_start_at 
      ? new Date(hospital.subscription_start_at).toISOString().split('T')[0]
      : today.toISOString().split('T')[0];
    
    // Calculate end date based on billing period
    let endDate = '';
    if (currentPlan === 'MONTHLY') {
      const end = new Date(startDate);
      end.setMonth(end.getMonth() + 1);
      endDate = end.toISOString().split('T')[0];
    } else if (currentPlan === 'YEARLY') {
      const end = new Date(startDate);
      end.setFullYear(end.getFullYear() + 1);
      endDate = end.toISOString().split('T')[0];
    } else if (hospital.trial_end_at) {
      endDate = new Date(hospital.trial_end_at).toISOString().split('T')[0];
    } else {
      // Default to 1 month from today
      const end = new Date(today);
      end.setMonth(end.getMonth() + 1);
      endDate = end.toISOString().split('T')[0];
    }
    
    setPlanFormData({
      plan_type: currentPlanType,
      billing_period: currentPlan || 'MONTHLY',
      start_date: startDate,
      end_date: endDate
    });
    setShowEditPlanModal(true);
  };

  const handleUpdatePlan = async () => {
    if (!selectedPlanHospital) return;

    // Validation
    if (!planFormData.start_date || !planFormData.end_date) {
      toast({
        title: 'Error',
        description: 'Please select both start and end dates',
        variant: 'destructive'
      });
      return;
    }

    if (new Date(planFormData.start_date) >= new Date(planFormData.end_date)) {
      toast({
        title: 'Error',
        description: 'End date must be after start date',
        variant: 'destructive'
      });
      return;
    }

    // For Silver/Gold plans, billing period is required
    if ((planFormData.plan_type === 'SILVER' || planFormData.plan_type === 'GOLD') && !planFormData.billing_period) {
      toast({
        title: 'Error',
        description: 'Please select a billing period (Monthly or Yearly)',
        variant: 'destructive'
      });
      return;
    }

    try {
      const adminData = JSON.parse(localStorage.getItem('admin_user'));
      
      // Prepare request data
      const requestData = {
        hospital_id: selectedPlanHospital.id,
        plan_type: planFormData.plan_type,
        plan: planFormData.plan_type === 'TRIAL' ? 'TRIAL' : planFormData.billing_period,
        start_at: `${planFormData.start_date} 00:00:00`,
        end_at: `${planFormData.end_date} 23:59:59`,
        admin_id: adminData.id
      };

      const response = await apiRequest('admin_update_subscription.php', {
        method: 'POST',
        body: JSON.stringify(requestData)
      });

      if (response.success) {
        toast({
          title: 'Success',
          description: 'Hospital plan updated successfully',
        });
        setShowEditPlanModal(false);
        setSelectedPlanHospital(null);
        fetchHospitals(); // Refresh hospitals list
      } else {
        throw new Error(response.error || 'Failed to update plan');
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to update plan',
        variant: 'destructive'
      });
    }
  };

  const calculateEndDate = (startDate, billingPeriod) => {
    if (!startDate) return '';
    const start = new Date(startDate);
    if (billingPeriod === 'MONTHLY') {
      start.setMonth(start.getMonth() + 1);
    } else if (billingPeriod === 'YEARLY') {
      start.setFullYear(start.getFullYear() + 1);
    }
    return start.toISOString().split('T')[0];
  };

  const handlePlanTypeChange = (planType) => {
    setPlanFormData({
      ...planFormData,
      plan_type: planType,
      billing_period: planType === 'TRIAL' ? 'MONTHLY' : planFormData.billing_period
    });
    
    // Auto-calculate end date if start date exists
    if (planFormData.start_date && planType !== 'TRIAL') {
      const endDate = calculateEndDate(planFormData.start_date, planFormData.billing_period);
      setPlanFormData(prev => ({ ...prev, end_date: endDate }));
    }
  };

  const handleBillingPeriodChange = (billingPeriod) => {
    setPlanFormData({
      ...planFormData,
      billing_period: billingPeriod
    });
    
    // Auto-calculate end date if start date exists
    if (planFormData.start_date) {
      const endDate = calculateEndDate(planFormData.start_date, billingPeriod);
      setPlanFormData(prev => ({ ...prev, end_date: endDate }));
    }
  };

  const handleStartDateChange = (startDate) => {
    setPlanFormData({
      ...planFormData,
      start_date: startDate
    });
    
    // Auto-calculate end date if billing period is set and plan is not TRIAL
    if (startDate && planFormData.plan_type !== 'TRIAL' && planFormData.billing_period) {
      const endDate = calculateEndDate(startDate, planFormData.billing_period);
      setPlanFormData(prev => ({ ...prev, end_date: endDate }));
    }
  };

  const handleSendPlanEmail = async (hospital) => {
    try {
      // Check if hospital has an active subscription (MONTHLY or YEARLY)
      if (hospital.current_plan !== 'MONTHLY' && hospital.current_plan !== 'YEARLY') {
        toast({
          title: 'Error',
          description: 'Hospital must have an active paid subscription (Monthly or Yearly) to send invoice email',
          variant: 'destructive'
        });
        return;
      }

      // Generate invoice using hospital_id directly
      const invoiceResponse = await apiRequest('generate_invoice.php', {
        method: 'POST',
        body: JSON.stringify({
          hospital_id: hospital.id
        })
      });

      if (!invoiceResponse.success || !invoiceResponse.invoice) {
        throw new Error(invoiceResponse.error || 'Failed to generate invoice');
      }

      const invoiceData = invoiceResponse.invoice;

      // Generate PDF
      const doc = <InvoicePDF invoiceData={invoiceData} />;
      const pdfBlob = await pdf(doc).toBlob();
      
      // Save PDF to server
      const formData = new FormData();
      formData.append('pdf', pdfBlob, `invoice_${invoiceData.hospital_name.replace(/\s+/g, '_')}_${new Date(invoiceData.invoice_date).toISOString().split('T')[0]}.pdf`);
      formData.append('hospital_id', hospital.id);
      formData.append('hospital_name', invoiceData.hospital_name);
      formData.append('invoice_date', invoiceData.invoice_date);

      // Get API base URL
      const API_BASE_URL = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
        ? 'http://localhost:8001/api'
        : `${window.location.protocol}//${window.location.hostname}/api/api`;
      
      const saveResponse = await fetch(`${API_BASE_URL}/save_invoice_pdf.php`, {
        method: 'POST',
        body: formData
      });

      const saveResult = await saveResponse.json();
      if (!saveResult.success) {
        throw new Error(saveResult.error || 'Failed to save invoice PDF');
      }

      // Send email with PDF
      const emailResponse = await apiRequest('send_invoice_email.php', {
        method: 'POST',
        body: JSON.stringify({
          hospital_id: hospital.id,
          hospital_email: hospital.email,
          hospital_name: invoiceData.hospital_name,
          invoice_pdf_path: saveResult.filepath,
          plan_type: invoiceData.plan_type,
          payment_plan: invoiceData.payment_plan,
          subscription_end: invoiceData.subscription_end
        })
      });

      if (emailResponse.success) {
        toast({
          title: 'Success',
          description: `Invoice email sent successfully to ${emailResponse.email_sent_to || hospital.email}.`,
        });
      } else {
        throw new Error(emailResponse.error || emailResponse.message || 'Failed to send invoice email');
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to send invoice',
        variant: 'destructive'
      });
    }
  };

  const handleSendInvoice = async (request) => {
    try {
      // Generate invoice data
      const invoiceData = await handleGenerateInvoice(request);
      if (!invoiceData) return;

      // Generate PDF
      const doc = <InvoicePDF invoiceData={invoiceData} />;
      const pdfBlob = await pdf(doc).toBlob();
      
      // Save PDF to server
      const formData = new FormData();
      formData.append('pdf', pdfBlob, `invoice_${invoiceData.hospital_name.replace(/\s+/g, '_')}_${new Date(invoiceData.invoice_date).toISOString().split('T')[0]}.pdf`);
      formData.append('hospital_id', request.hospital_id);
      formData.append('hospital_name', invoiceData.hospital_name);
      formData.append('invoice_date', invoiceData.invoice_date);

      // Get API base URL
      const API_BASE_URL = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
        ? 'http://localhost:8001/api'
        : `${window.location.protocol}//${window.location.hostname}/api/api`;
      
      const saveResponse = await fetch(`${API_BASE_URL}/save_invoice_pdf.php`, {
        method: 'POST',
        body: formData
      });

      const saveResult = await saveResponse.json();
      if (!saveResult.success) {
        throw new Error(saveResult.error || 'Failed to save invoice PDF');
      }

      // Send email with PDF
      // Use hospital_email from request, or fallback to invoiceData
      const hospitalEmail = request.hospital_email || invoiceData.hospital_email;
      
      if (!hospitalEmail) {
        throw new Error('Hospital email not found. Please ensure the hospital has a registered email address.');
      }

      const emailResponse = await apiRequest('send_invoice_email.php', {
        method: 'POST',
        body: JSON.stringify({
          hospital_id: request.hospital_id,
          hospital_email: hospitalEmail,
          hospital_name: invoiceData.hospital_name,
          invoice_pdf_path: saveResult.filepath,
          plan_type: invoiceData.plan_type,
          payment_plan: invoiceData.payment_plan,
          subscription_end: invoiceData.subscription_end
        })
      });

      if (emailResponse.success) {
        toast({
          title: 'Success',
          description: `Invoice email sent successfully to ${emailResponse.email_sent_to || hospitalEmail}.`,
        });
      } else {
        throw new Error(emailResponse.error || emailResponse.message || 'Failed to send invoice email');
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to send invoice',
        variant: 'destructive'
      });
    }
  };

  useEffect(() => {
    fetchHospitals();
  }, [filterStatus]);

  const handleApprove = async (hospitalId) => {
    if (!confirm('Are you sure you want to approve this hospital? This will activate their trial period.')) {
      return;
    }

    try {
      const adminData = JSON.parse(localStorage.getItem('admin_user'));
      const response = await apiRequest('admin_approve_hospital.php', {
        method: 'POST',
        body: JSON.stringify({
          hospital_id: hospitalId,
          admin_id: adminData.id
        })
      });

      if (response.success) {
        toast({
          title: 'Success',
          description: 'Hospital approved successfully. Trial period activated.',
        });
        fetchHospitals();
      } else {
        throw new Error(response.error || 'Failed to approve hospital');
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to approve hospital',
        variant: 'destructive'
      });
    }
  };

  const handleReject = async (hospitalId) => {
    const reason = prompt('Enter rejection reason (optional):');
    
    if (reason === null) return; // User cancelled

    try {
      const adminData = JSON.parse(localStorage.getItem('admin_user'));
      const response = await apiRequest('admin_reject_hospital.php', {
        method: 'POST',
        body: JSON.stringify({
          hospital_id: hospitalId,
          admin_id: adminData.id,
          reason: reason || null
        })
      });

      if (response.success) {
        toast({
          title: 'Success',
          description: 'Hospital rejected successfully.',
        });
        fetchHospitals();
      } else {
        throw new Error(response.error || 'Failed to reject hospital');
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to reject hospital',
        variant: 'destructive'
      });
    }
  };

  const handleSuspend = async (hospitalId) => {
    if (!confirm('Are you sure you want to suspend this hospital? This will block their access.')) {
      return;
    }

    try {
      const adminData = JSON.parse(localStorage.getItem('admin_user'));
      const response = await apiRequest('admin_suspend_hospital.php', {
        method: 'POST',
        body: JSON.stringify({
          hospital_id: hospitalId,
          admin_id: adminData.id
        })
      });

      if (response.success) {
        toast({
          title: 'Success',
          description: 'Hospital suspended successfully.',
        });
        fetchHospitals();
      } else {
        throw new Error(response.error || 'Failed to suspend hospital');
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to suspend hospital',
        variant: 'destructive'
      });
    }
  };

  const handleActivate = async (hospitalId) => {
    if (!confirm('Are you sure you want to activate this hospital? This will restore their access.')) {
      return;
    }

    try {
      const adminData = JSON.parse(localStorage.getItem('admin_user'));
      const response = await apiRequest('admin_activate_hospital.php', {
        method: 'POST',
        body: JSON.stringify({
          hospital_id: hospitalId,
          admin_id: adminData.id
        })
      });

      if (response.success) {
        toast({
          title: 'Success',
          description: 'Hospital activated successfully.',
        });
        fetchHospitals();
      } else {
        throw new Error(response.error || 'Failed to activate hospital');
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to activate hospital',
        variant: 'destructive'
      });
    }
  };

  const handleDelete = async (hospitalId) => {
    if (!confirm('WARNING: This will permanently delete the hospital and all its data. This action cannot be undone. Continue?')) {
      return;
    }

    try {
      const adminData = JSON.parse(localStorage.getItem('admin_user'));
      const response = await apiRequest('admin_delete_hospital.php', {
        method: 'POST',
        body: JSON.stringify({
          hospital_id: hospitalId,
          admin_id: adminData.id
        })
      });

      if (response.success) {
        toast({
          title: 'Success',
          description: 'Hospital deleted successfully.',
        });
        fetchHospitals();
      } else {
        throw new Error(response.error || 'Failed to delete hospital');
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to delete hospital',
        variant: 'destructive'
      });
    }
  };

  const handleEdit = (hospital) => {
    setSelectedHospital(hospital);
    setEditFormData({
      name: hospital.name,
      email: hospital.email,
      phone: hospital.phone || '',
      address: hospital.address || '',
      admin_password: '',
      confirmPassword: ''
    });
    setShowPassword(false);
    setShowConfirmPassword(false);
    setLogoFile(null);
    setLogoPreview(null);
    const logoUrl = hospital.logo_url || null;
    setExistingLogo(logoUrl);
    setOriginalLogo(logoUrl); // Store original logo URL for comparison
    setShowEditModal(true);
  };

  const handleLogoChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        toast({
          title: 'Invalid file type',
          description: 'Please upload an image file (JPG, PNG, GIF, or WebP)',
          variant: 'destructive'
        });
        return;
      }

      // Validate file size (5MB max)
      const maxSize = 5 * 1024 * 1024; // 5MB
      if (file.size > maxSize) {
        toast({
          title: 'File too large',
          description: 'Logo file must be less than 5MB',
          variant: 'destructive'
        });
        return;
      }

      setLogoFile(file);
      // Don't clear existingLogo - we'll show preview instead
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveLogo = () => {
    setLogoFile(null);
    setLogoPreview(null);
    setExistingLogo(null);
    // Reset file input
    const fileInput = document.getElementById('edit_hospital_logo');
    if (fileInput) {
      fileInput.value = '';
    }
  };

  const handleUpdate = async () => {
    // Validate password if provided
    if (editFormData.admin_password) {
      if (editFormData.admin_password.length < 6) {
        toast({
          title: 'Error',
          description: 'Password must be at least 6 characters long',
          variant: 'destructive'
        });
        return;
      }
      
      if (editFormData.admin_password !== editFormData.confirmPassword) {
        toast({
          title: 'Error',
          description: 'Passwords do not match',
          variant: 'destructive'
        });
        return;
      }
    }

    try {
      // Create FormData for file upload
      const formDataToSend = new FormData();
      formDataToSend.append('hospital_id', selectedHospital.id);
      formDataToSend.append('name', editFormData.name);
      formDataToSend.append('email', editFormData.email);
      formDataToSend.append('phone', editFormData.phone || '');
      formDataToSend.append('address', editFormData.address || '');
      
      // Only include password if provided
      if (editFormData.admin_password) {
        formDataToSend.append('admin_password', editFormData.admin_password);
      }
      
      // Append logo file if selected
      if (logoFile) {
        formDataToSend.append('hospital_logo', logoFile);
      }
      
      // If logo was removed (originalLogo existed but existingLogo is now null and no new file selected)
      if (originalLogo && !existingLogo && !logoFile && !logoPreview) {
        formDataToSend.append('remove_logo', '1');
      }

      // Get API base URL - use same logic as api.js
      const getApiBaseUrl = () => {
        if (import.meta.env.VITE_API_BASE_URL) {
          return import.meta.env.VITE_API_BASE_URL.replace(/\/+$/, '');
        }
        if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
          return 'http://localhost:8001/api';
        }
        // Production: Use /api/api path since files are in api/api/ folder
        return `${window.location.protocol}//${window.location.hostname}/api/api`;
      };
      const API_BASE_URL = getApiBaseUrl();
      const token = localStorage.getItem('admin_session_token');

      let response;
      let responseData;
      
      try {
        response = await fetch(`${API_BASE_URL}/admin_update_hospital.php`, {
          method: 'POST',
          headers: token ? {
            'Authorization': `Bearer ${token}`
          } : {},
          body: formDataToSend
        });
      } catch (fetchError) {
        // Handle network errors
        if (fetchError.message.includes('Failed to fetch') || fetchError.message.includes('ERR_CONNECTION_REFUSED') || fetchError.message.includes('network')) {
          throw new Error('Cannot connect to the server. Please ensure the backend server is running on http://localhost:8001');
        }
        throw fetchError;
      }

      // Try to parse JSON response
      try {
        const text = await response.text();
        if (!text) {
          throw new Error('Empty response from server');
        }
        responseData = JSON.parse(text);
      } catch (jsonError) {
        console.error('Failed to parse response as JSON:', jsonError);
        throw new Error('Invalid response from server. Please check if the backend server is running correctly.');
      }

      if (!response.ok) {
        throw new Error(responseData.error || responseData.message || `Update failed (${response.status})`);
      }

      if (responseData.success) {
        toast({
          title: 'Success',
          description: 'Hospital updated successfully.',
        });
        setShowEditModal(false);
        setLogoFile(null);
        setLogoPreview(null);
        setExistingLogo(null);
        setOriginalLogo(null);
        fetchHospitals();
      } else {
        throw new Error(responseData.error || responseData.message || 'Failed to update hospital');
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to update hospital',
        variant: 'destructive'
      });
    }
  };

  const handleSendEmail = async (hospitalId) => {
    try {
      const response = await apiRequest('admin_send_welcome_email.php', {
        method: 'POST',
        body: JSON.stringify({
          hospital_id: hospitalId
        })
      });

      if (response.success) {
        toast({
          title: 'Success',
          description: `Welcome email sent successfully to ${response.email}`,
        });
        // Refresh hospitals list to update welcome email status
        fetchHospitals();
      } else {
        throw new Error(response.error || response.message || 'Failed to send email');
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to send welcome email',
        variant: 'destructive'
      });
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_session_token');
    localStorage.removeItem('admin_user');
    navigate('/admin/hub/login');
  };

  const getStatusBadge = (status) => {
    const badges = {
      'PENDING_APPROVAL': { bg: 'bg-yellow-500/20', text: 'text-yellow-400', icon: Clock, label: 'Pending' },
      'APPROVED': { bg: 'bg-green-500/20', text: 'text-green-400', icon: CheckCircle2, label: 'Approved' },
      'REJECTED': { bg: 'bg-red-500/20', text: 'text-red-400', icon: XCircle, label: 'Rejected' },
      'SUSPENDED': { bg: 'bg-orange-500/20', text: 'text-orange-400', icon: AlertCircle, label: 'Suspended' }
    };
    
    const badge = badges[status] || badges['PENDING_APPROVAL'];
    const Icon = badge.icon;
    
    return (
      <span className={`inline-flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`}>
        <Icon className="h-3 w-3" />
        <span>{badge.label}</span>
      </span>
    );
  };

  const getHospitalUrl = (slug) => {
    const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    if (isLocalhost) {
      return `http://localhost:5174/${slug}`;
    }
    const protocol = window.location.protocol;
    const host = window.location.host;
    return `${protocol}//${host}/${slug}`;
  };

  const filteredHospitals = hospitals.filter(hospital => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        hospital.name.toLowerCase().includes(query) ||
        hospital.email.toLowerCase().includes(query) ||
        hospital.slug.toLowerCase().includes(query)
      );
    }
    return true;
  });

  return (
    <>
      <Helmet>
        <title>Dental Hub Admin Dashboard</title>
      </Helmet>
      
      <div className="min-h-screen">
        {/* Header */}
        <header className="glass-effect border-b border-gray-700 sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-16">
              <div className="flex items-center space-x-3">
                <Shield className="h-6 w-6 text-blue-400" />
                <span className="text-xl font-bold gradient-text">Dental Hub Admin</span>
              </div>
              <div className="flex items-center space-x-4">
                {adminUser && (
                  <span className="text-white text-sm">Welcome, {adminUser.name}</span>
                )}
                <Button
                  variant="ghost"
                  onClick={handleLogout}
                  className="text-white hover:text-red-400"
                >
                  <LogOut className="h-4 w-4 mr-2" />
                  Logout
                </Button>
              </div>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Page Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold gradient-text mb-2">Hospital Management</h1>
            <p className="text-gray-300">Manage hospital registrations, approvals, and subscriptions</p>
          </div>

          {/* Tabs */}
          <div className="flex space-x-4 mb-6 border-b border-gray-700">
            <button
              onClick={() => setActiveTab('hospitals')}
              className={`px-4 py-2 font-medium transition-colors ${
                activeTab === 'hospitals'
                  ? 'text-blue-400 border-b-2 border-blue-400'
                  : 'text-gray-400 hover:text-gray-300'
              }`}
            >
              Hospitals
            </button>
            <button
              onClick={() => setActiveTab('enrollments')}
              className={`px-4 py-2 font-medium transition-colors relative ${
                activeTab === 'enrollments'
                  ? 'text-blue-400 border-b-2 border-blue-400'
                  : 'text-gray-400 hover:text-gray-300'
              }`}
            >
              Enrollment Requests
              {enrollmentRequests.length > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                  {enrollmentRequests.length}
                </span>
              )}
            </button>
          </div>

          {activeTab === 'hospitals' ? (
            <>
              {/* Filters and Search */}
              <Card className="glass-effect border-gray-700 mb-6">
                <CardContent className="p-4">
                  <div className="flex flex-col sm:flex-row gap-4">
                    <div className="flex-1">
                      <div className="relative">
                        <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                        <Input
                          placeholder="Search hospitals by name, email, or slug..."
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          className="pl-10 bg-gray-800 border-gray-600 text-white"
                        />
                      </div>
                    </div>
                    <div className="w-full sm:w-48">
                      <Select value={filterStatus} onValueChange={setFilterStatus}>
                        <SelectTrigger className="bg-gray-800 border-gray-600 text-white">
                          <Filter className="h-4 w-4 mr-2" />
                          <SelectValue placeholder="Filter by status" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Status</SelectItem>
                          <SelectItem value="PENDING_APPROVAL">Pending Approval</SelectItem>
                          <SelectItem value="APPROVED">Approved</SelectItem>
                          <SelectItem value="REJECTED">Rejected</SelectItem>
                          <SelectItem value="SUSPENDED">Suspended</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <Button
                      onClick={fetchHospitals}
                      variant="outline"
                      className="border-blue-400 text-blue-400 hover:bg-blue-400 hover:text-white"
                    >
                      <RefreshCw className="h-4 w-4 mr-2" />
                      Refresh
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Hospitals List */}
          {loading ? (
            <div className="text-center py-12">
              <RefreshCw className="h-8 w-8 text-blue-400 animate-spin mx-auto mb-4" />
              <p className="text-gray-300">Loading hospitals...</p>
            </div>
          ) : filteredHospitals.length === 0 ? (
            <Card className="glass-effect border-gray-700">
              <CardContent className="p-12 text-center">
                <Building2 className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-300">No hospitals found</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 gap-4">
              {filteredHospitals.map((hospital) => (
                <motion.div
                  key={hospital.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <Card className="glass-effect border-gray-700 card-hover">
                    <CardContent className="p-6">
                      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-start justify-between mb-2">
                            <div>
                              <h3 className="text-xl font-semibold text-white mb-1">{hospital.name}</h3>
                              <p className="text-gray-400 text-sm">{hospital.slug}</p>
                            </div>
                            {getStatusBadge(hospital.status)}
                          </div>
                          
                          {/* Profile and Plan Sections - Side by Side */}
                          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                            {/* Profile Section */}
                            <div className="p-3 bg-gray-800/50 rounded-lg border border-gray-700">
                              <div className="flex items-center justify-between mb-3">
                                <h4 className="text-sm font-semibold text-white">Profile</h4>
                                <div className="flex gap-2">
                                  <Button
                                    onClick={() => handleEdit(hospital)}
                                    variant="outline"
                                    size="sm"
                                    className="border-blue-400 text-blue-400 hover:bg-blue-400 hover:text-white h-7 px-2 text-xs"
                                  >
                                    <Edit className="h-3 w-3 mr-1" />
                                    Edit
                                  </Button>
                                  <Button
                                    onClick={() => handleSendEmail(hospital.id)}
                                    variant="outline"
                                    size="sm"
                                    className="border-green-400 text-green-400 hover:bg-green-400 hover:text-white h-7 px-2 text-xs"
                                  >
                                    <Mail className="h-3 w-3 mr-1" />
                                    Send Email
                                  </Button>
                                </div>
                              </div>
                              <div className="space-y-1 text-sm">
                                {hospital.clinic_admin_name && (
                                  <div className="flex items-center space-x-2 text-gray-300">
                                    <User className="h-4 w-4 text-gray-400" />
                                    <span><span className="text-gray-400">Clinic Admin:</span> {hospital.clinic_admin_name}</span>
                                  </div>
                                )}
                                <div className="flex items-center space-x-2 text-gray-300">
                                  <Mail className="h-4 w-4 text-gray-400" />
                                  <span>{hospital.email}</span>
                                </div>
                                {hospital.phone && (
                                  <div className="flex items-center space-x-2 text-gray-300">
                                    <Phone className="h-4 w-4 text-gray-400" />
                                    <span>Phone: {hospital.phone}</span>
                                  </div>
                                )}
                                <div className="flex items-center space-x-2 text-gray-300">
                                  <Users className="h-4 w-4 text-gray-400" />
                                  <span>{hospital.active_user_count || 0} active users</span>
                                </div>
                                <div className="flex items-center space-x-2 text-gray-300">
                                  <Mail className="h-4 w-4 text-gray-400" />
                                  <span>Welcome Email: {hospital.welcome_email_sent_at ? <span className="text-green-400">Sent</span> : <span className="text-gray-400">Not Sent</span>}</span>
                                </div>
                                {hospital.slug && (
                                  <div className="flex items-center space-x-2 text-gray-300">
                                    <Globe className="h-4 w-4 text-blue-400" />
                                    <span className="text-gray-400">Website Link:</span>
                                    <a
                                      href={getHospitalUrl(hospital.slug)}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-blue-400 hover:text-blue-300 hover:underline flex items-center space-x-1"
                                    >
                                      <span>{getHospitalUrl(hospital.slug)}</span>
                                      <ExternalLink className="h-3 w-3" />
                                    </a>
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* Plan Section */}
                            <div className="p-3 bg-gray-800/50 rounded-lg border border-gray-700">
                              <div className="flex items-center justify-between mb-3">
                                <h4 className="text-sm font-semibold text-white">Plan</h4>
                                <div className="flex gap-2">
                                  <Button
                                    onClick={() => handleEditPlan(hospital)}
                                    variant="outline"
                                    size="sm"
                                    className="border-blue-400 text-blue-400 hover:bg-blue-400 hover:text-white h-7 px-2 text-xs"
                                  >
                                    <Edit className="h-3 w-3 mr-1" />
                                    Edit
                                  </Button>
                                  <Button
                                    onClick={() => handleSendPlanEmail(hospital)}
                                    variant="outline"
                                    size="sm"
                                    disabled={!hospital.plan_type || hospital.plan_type === 'TRIAL' || (hospital.current_plan !== 'MONTHLY' && hospital.current_plan !== 'YEARLY')}
                                    className="border-green-400 text-green-400 hover:bg-green-400 hover:text-white h-7 px-2 text-xs disabled:opacity-50 disabled:cursor-not-allowed"
                                  >
                                    <Send className="h-3 w-3 mr-1" />
                                    Send Invoice
                                  </Button>
                                </div>
                              </div>
                              <div className="space-y-1 text-sm">
                                {/* Plan Type: Trial/Silver/Gold */}
                                <div className="flex items-center space-x-2 text-gray-300">
                                  <Shield className="h-4 w-4 text-gray-400" />
                                  <span><span className="text-gray-400">Plan Type:</span> {
                                    hospital.plan_type === 'SILVER' ? 'Silver' : 
                                    hospital.plan_type === 'GOLD' ? 'Gold' : 
                                    'Trial'
                                  }</span>
                                </div>
                                {/* Plan: Monthly/Yearly (only if enrolled) */}
                                {(hospital.current_plan === 'MONTHLY' || hospital.current_plan === 'YEARLY') && (
                                  <>
                                    <div className="flex items-center space-x-2 text-gray-300">
                                      <Calendar className="h-4 w-4 text-gray-400" />
                                      <span><span className="text-gray-400">Plan:</span> {hospital.current_plan === 'MONTHLY' ? 'Monthly' : 'Yearly'}</span>
                                    </div>
                                    {hospital.subscription_start_at && (
                                      <div className="flex items-center space-x-2 text-gray-300">
                                        <Calendar className="h-4 w-4 text-gray-400" />
                                        <span><span className="text-gray-400">Enrolled Date:</span> {new Date(hospital.subscription_start_at).toLocaleDateString()}</span>
                                      </div>
                                    )}
                                    {hospital.subscription_end_at && (
                                      <div className="flex items-center space-x-2 text-gray-300">
                                        <Calendar className="h-4 w-4 text-gray-400" />
                                        <span><span className="text-gray-400">Next renewal date:</span> {new Date(hospital.subscription_end_at).toLocaleDateString()}</span>
                                      </div>
                                    )}
                                  </>
                                )}
                                {/* Trial Period (if not enrolled) */}
                                {(!hospital.current_plan || (hospital.current_plan !== 'MONTHLY' && hospital.current_plan !== 'YEARLY')) && hospital.trial_end_at && (
                                  <div className="flex items-center space-x-2 text-gray-300">
                                    <Calendar className="h-4 w-4 text-gray-400" />
                                    <span><span className="text-gray-400">Trial ends:</span> {new Date(hospital.trial_end_at).toLocaleDateString()}</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex flex-wrap gap-2 lg:flex-col">
                          {hospital.status === 'PENDING_APPROVAL' && (
                            <>
                              <Button
                                onClick={() => handleApprove(hospital.id)}
                                className="medical-gradient text-white hover:opacity-90 flex-1"
                                size="sm"
                              >
                                <CheckCircle2 className="h-4 w-4 mr-2" />
                                Approve
                              </Button>
                              <Button
                                onClick={() => handleReject(hospital.id)}
                                variant="destructive"
                                size="sm"
                                className="flex-1"
                              >
                                <XCircle className="h-4 w-4 mr-2" />
                                Reject
                              </Button>
                            </>
                          )}
                          
                          {hospital.status === 'APPROVED' && (
                            <Button
                              onClick={() => handleSuspend(hospital.id)}
                              variant="outline"
                              className="border-orange-400 text-orange-400 hover:bg-orange-400 hover:text-white"
                              size="sm"
                            >
                              <AlertCircle className="h-4 w-4 mr-2" />
                              Suspend
                            </Button>
                          )}
                          
                          {hospital.status === 'SUSPENDED' && (
                            <Button
                              onClick={() => handleActivate(hospital.id)}
                              variant="outline"
                              className="border-green-400 text-green-400 hover:bg-green-400 hover:text-white"
                              size="sm"
                            >
                              <CheckCircle2 className="h-4 w-4 mr-2" />
                              Activate
                            </Button>
                          )}
                          
                          <Button
                            onClick={() => handleDelete(hospital.id)}
                            variant="destructive"
                            size="sm"
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          )}
            </>
          ) : (
            <>
              {/* Enrollment Requests */}
              <div className="mb-6">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-xl font-semibold text-white">Enrollment Requests</h2>
                  <Button
                    onClick={fetchEnrollmentRequests}
                    variant="outline"
                    className="border-blue-400 text-blue-400 hover:bg-blue-400 hover:text-white"
                  >
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Refresh
                  </Button>
                </div>
                
                {/* Filter */}
                <div className="mb-4">
                  <Select value={enrollmentFilter} onValueChange={setEnrollmentFilter}>
                    <SelectTrigger className="bg-gray-800 border-gray-600 text-white w-48">
                      <Filter className="h-4 w-4 mr-2" />
                      <SelectValue placeholder="Filter requests" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Requests</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="approved">Approved</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {loadingEnrollments ? (
                <div className="text-center py-12">
                  <RefreshCw className="h-8 w-8 text-blue-400 animate-spin mx-auto mb-4" />
                  <p className="text-gray-300">Loading enrollment requests...</p>
                </div>
              ) : (
                <>
                  {/* Pending Enrollment Requests */}
                  {(enrollmentFilter === 'all' || enrollmentFilter === 'pending') && (
                    <div className="mb-6">
                      <h3 className="text-lg font-semibold text-white mb-4">Pending Enrollment Requests</h3>
                      {enrollmentRequests.length === 0 ? (
                        <Card className="glass-effect border-gray-700">
                          <CardContent className="p-12 text-center">
                            <CheckCircle2 className="h-12 w-12 text-green-400 mx-auto mb-4" />
                            <p className="text-gray-300">No pending enrollment requests</p>
                          </CardContent>
                        </Card>
                      ) : (
                        <div className="grid grid-cols-1 gap-4">
                          {enrollmentRequests.map((request) => (
                            <motion.div
                              key={request.id}
                              initial={{ opacity: 0, y: 20 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ duration: 0.3 }}
                            >
                              <Card className="glass-effect border-gray-700 card-hover">
                                <CardContent className="p-6">
                                  <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                                    <div className="flex-1">
                                      <div className="flex items-start justify-between mb-2">
                                        <div>
                                          <h3 className="text-xl font-semibold text-white mb-1">{request.hospital_name}</h3>
                                          <p className="text-gray-400 text-sm">{request.hospital_slug}</p>
                                        </div>
                                        <span className="px-3 py-1 bg-yellow-500/20 text-yellow-400 rounded-full text-xs font-medium">
                                          PENDING
                                        </span>
                                      </div>
                                      
                                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-4 text-sm">
                                        <div className="flex items-center space-x-2 text-gray-300">
                                          <Shield className="h-4 w-4 text-gray-400" />
                                          <span>Plan: <span className="font-semibold">{request.plan_type}</span></span>
                                        </div>
                                        <div className="flex items-center space-x-2 text-gray-300">
                                          <Calendar className="h-4 w-4 text-gray-400" />
                                          <span>Payment: <span className="font-semibold">{request.payment_plan === 'MONTHLY' ? 'Monthly' : 'Yearly'}</span></span>
                                        </div>
                                        <div className="flex items-center space-x-2 text-gray-300">
                                          <Clock className="h-4 w-4 text-gray-400" />
                                          <span>Requested: {new Date(request.requested_at).toLocaleDateString()}</span>
                                        </div>
                                        {request.hospital_email && (
                                          <div className="flex items-center space-x-2 text-gray-300">
                                            <Mail className="h-4 w-4 text-gray-400" />
                                            <span>{request.hospital_email}</span>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                    
                                    <div className="flex flex-wrap gap-2 lg:flex-col">
                                      <Button
                                        onClick={() => handleApproveEnrollment(request.id)}
                                        className="medical-gradient text-white hover:opacity-90 flex-1"
                                        size="sm"
                                      >
                                        <CheckCircle2 className="h-4 w-4 mr-2" />
                                        Approve Enrollment
                                      </Button>
                                    </div>
                                  </div>
                                </CardContent>
                              </Card>
                            </motion.div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Approved Enrollment Requests */}
                  {(enrollmentFilter === 'all' || enrollmentFilter === 'approved') && (
                    <div>
                      <h3 className="text-lg font-semibold text-white mb-4">Approved Enrollment Requests</h3>
                      {approvedEnrollmentRequests.length === 0 ? (
                        <Card className="glass-effect border-gray-700">
                          <CardContent className="p-12 text-center">
                            <CheckCircle2 className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                            <p className="text-gray-300">No approved enrollment requests</p>
                          </CardContent>
                        </Card>
                      ) : (
                        <div className="grid grid-cols-1 gap-4">
                          {approvedEnrollmentRequests.map((request) => (
                            <motion.div
                              key={request.id}
                              initial={{ opacity: 0, y: 20 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ duration: 0.3 }}
                            >
                              <Card className="glass-effect border-gray-700 card-hover">
                                <CardContent className="p-6">
                                  <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                                    <div className="flex-1">
                                      <div className="flex items-start justify-between mb-2">
                                        <div>
                                          <h3 className="text-xl font-semibold text-white mb-1">{request.hospital_name}</h3>
                                          <p className="text-gray-400 text-sm">{request.hospital_slug}</p>
                                        </div>
                                        <span className="px-3 py-1 bg-green-500/20 text-green-400 rounded-full text-xs font-medium">
                                          APPROVED
                                        </span>
                                      </div>
                                      
                                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-4 text-sm">
                                        <div className="flex items-center space-x-2 text-gray-300">
                                          <Shield className="h-4 w-4 text-gray-400" />
                                          <span>Plan: <span className="font-semibold">{request.plan_type}</span></span>
                                        </div>
                                        <div className="flex items-center space-x-2 text-gray-300">
                                          <Calendar className="h-4 w-4 text-gray-400" />
                                          <span>Payment: <span className="font-semibold">{request.payment_plan === 'MONTHLY' ? 'Monthly' : 'Yearly'}</span></span>
                                        </div>
                                        <div className="flex items-center space-x-2 text-gray-300">
                                          <User className="h-4 w-4 text-gray-400" />
                                          <span><span className="text-gray-400">Requested by:</span> {request.hospital_name}</span>
                                        </div>
                                        <div className="flex items-center space-x-2 text-gray-300">
                                          <Clock className="h-4 w-4 text-gray-400" />
                                          <span><span className="text-gray-400">Requested Date:</span> {new Date(request.requested_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
                                        </div>
                                        {request.approved_by_name && (
                                          <div className="flex items-center space-x-2 text-gray-300">
                                            <User className="h-4 w-4 text-gray-400" />
                                            <span><span className="text-gray-400">Approved by:</span> {request.approved_by_name}</span>
                                          </div>
                                        )}
                                        {request.approved_at && (
                                          <div className="flex items-center space-x-2 text-gray-300">
                                            <Calendar className="h-4 w-4 text-gray-400" />
                                            <span><span className="text-gray-400">Approved Date:</span> {new Date(request.approved_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
                                          </div>
                                        )}
                                        {request.subscription_start_at && (
                                          <div className="flex items-center space-x-2 text-gray-300">
                                            <Calendar className="h-4 w-4 text-gray-400" />
                                            <span><span className="text-gray-400">Enrolled Date:</span> {new Date(request.subscription_start_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
                                          </div>
                                        )}
                                        {request.subscription_end_at && (
                                          <div className="flex items-center space-x-2 text-gray-300">
                                            <Calendar className="h-4 w-4 text-gray-400" />
                                            <span><span className="text-gray-400">Next Renewal:</span> {new Date(request.subscription_end_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                    
                                    <div className="flex flex-wrap gap-2 lg:flex-col">
                                      <Button
                                        onClick={() => handleViewInvoice(request)}
                                        variant="outline"
                                        className="border-blue-400 text-blue-400 hover:bg-blue-400 hover:text-white flex-1"
                                        size="sm"
                                      >
                                        <FileText className="h-4 w-4 mr-2" />
                                        View Invoice
                                      </Button>
                                      <Button
                                        onClick={() => handlePrintInvoice(request)}
                                        variant="outline"
                                        className="border-purple-400 text-purple-400 hover:bg-purple-400 hover:text-white flex-1"
                                        size="sm"
                                      >
                                        <Printer className="h-4 w-4 mr-2" />
                                        Print Invoice
                                      </Button>
                                      <Button
                                        onClick={() => handleSendInvoice(request)}
                                        variant="outline"
                                        className="border-green-400 text-green-400 hover:bg-green-400 hover:text-white flex-1"
                                        size="sm"
                                      >
                                        <Send className="h-4 w-4 mr-2" />
                                        Send Invoice
                                      </Button>
                                    </div>
                                  </div>
                                </CardContent>
                              </Card>
                            </motion.div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </main>

        {/* Edit Modal */}
        {showEditModal && selectedHospital && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="w-full max-w-3xl my-8"
            >
              <Card className="glass-effect border-gray-700">
                <CardHeader className="text-center p-6">
                  <div className="flex items-center justify-center space-x-2 mb-4">
                    <Building2 className="h-8 w-8 text-blue-400" />
                    <span className="text-2xl font-bold gradient-text">Dental Hub</span>
                  </div>
                  <CardTitle className="text-2xl text-white">Edit Hospital</CardTitle>
                  <CardDescription className="text-gray-300">
                    Update hospital information
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={(e) => { e.preventDefault(); handleUpdate(); }} className="space-y-6">
                    {/* Hospital Information Section */}
                    <div className="space-y-4">
                      <div className="flex items-center space-x-2 mb-4">
                        <Building2 className="h-5 w-5 text-blue-400" />
                        <h3 className="text-lg font-semibold text-white">Hospital Information</h3>
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="edit_hospital_name" className="text-white">
                          Hospital/Clinic Name <span className="text-red-400">*</span>
                        </Label>
                        <div className="relative">
                          <Building2 className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                          <Input
                            id="edit_hospital_name"
                            name="name"
                            type="text"
                            placeholder="Enter hospital or clinic name"
                            value={editFormData.name}
                            onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                            className="pl-10 bg-gray-800 border-gray-600 text-white"
                            required
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="edit_hospital_email" className="text-white">
                            Hospital Email <span className="text-red-400">*</span>
                          </Label>
                          <div className="relative">
                            <Mail className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                            <Input
                              id="edit_hospital_email"
                              name="email"
                              type="email"
                              placeholder="contact@hospital.com"
                              value={editFormData.email}
                              onChange={(e) => setEditFormData({ ...editFormData, email: e.target.value })}
                              className="pl-10 bg-gray-800 border-gray-600 text-white"
                              required
                            />
                          </div>
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="edit_hospital_phone" className="text-white">
                            Hospital Phone
                          </Label>
                          <div className="relative">
                            <Phone className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                            <Input
                              id="edit_hospital_phone"
                              name="phone"
                              type="tel"
                              placeholder="+1234567890"
                              value={editFormData.phone}
                              onChange={(e) => setEditFormData({ ...editFormData, phone: e.target.value })}
                              className="pl-10 bg-gray-800 border-gray-600 text-white"
                            />
                          </div>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="edit_hospital_address" className="text-white">
                            Hospital Address
                          </Label>
                          <div className="relative">
                            <MapPin className="absolute left-3 top-3 h-4 w-4 text-gray-400 z-10" />
                            <Textarea
                              id="edit_hospital_address"
                              name="address"
                              placeholder="123 Main St, City, State, PIN Code"
                              value={editFormData.address}
                              onChange={(e) => setEditFormData({ ...editFormData, address: e.target.value })}
                              className="pl-10 bg-gray-800 border-gray-600 text-white min-h-[100px] resize-y"
                              rows={4}
                            />
                          </div>
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="edit_hospital_logo" className="text-white">
                            Hospital Logo
                          </Label>
                          <div className="space-y-2">
                            {logoPreview ? (
                              <div className="relative">
                                <div className="relative border-2 border-gray-600 rounded-lg p-2 bg-gray-800">
                                  <img 
                                    src={logoPreview} 
                                    alt="Logo preview" 
                                    className="w-full h-32 object-contain rounded"
                                  />
                                  <button
                                    type="button"
                                    onClick={handleRemoveLogo}
                                    className="absolute top-1 right-1 p-1 bg-red-500 hover:bg-red-600 rounded-full text-white transition-colors"
                                    aria-label="Remove logo"
                                  >
                                    <X className="h-4 w-4" />
                                  </button>
                                </div>
                                <p className="text-xs text-gray-400 mt-1">{logoFile?.name}</p>
                                <p className="text-xs text-blue-400">New logo will replace existing one</p>
                              </div>
                            ) : existingLogo ? (
                              <div className="relative">
                                <div className="relative border-2 border-gray-600 rounded-lg p-2 bg-gray-800">
                                  <img 
                                    src={existingLogo} 
                                    alt="Current logo" 
                                    className="w-full h-32 object-contain rounded"
                                    onError={(e) => {
                                      e.target.style.display = 'none';
                                      e.target.nextElementSibling.style.display = 'block';
                                    }}
                                  />
                                  <div className="hidden w-full h-32 flex items-center justify-center text-gray-400 text-sm">
                                    Failed to load logo
                                  </div>
                                  <button
                                    type="button"
                                    onClick={handleRemoveLogo}
                                    className="absolute top-1 right-1 p-1 bg-red-500 hover:bg-red-600 rounded-full text-white transition-colors"
                                    aria-label="Remove logo"
                                  >
                                    <X className="h-4 w-4" />
                                  </button>
                                </div>
                                <p className="text-xs text-gray-400 mt-1">Current logo</p>
                                <label htmlFor="edit_hospital_logo">
                                  <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    className="w-full mt-2 border-blue-400 text-blue-400 hover:bg-blue-400 hover:text-white"
                                  >
                                    <Upload className="h-4 w-4 mr-2" />
                                    Change Logo
                                  </Button>
                                </label>
                                <input
                                  id="edit_hospital_logo"
                                  name="hospital_logo"
                                  type="file"
                                  accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                                  onChange={handleLogoChange}
                                  className="hidden"
                                />
                              </div>
                            ) : (
                              <div className="border-2 border-dashed border-gray-600 rounded-lg p-4 text-center hover:border-blue-500 transition-colors">
                                <label htmlFor="edit_hospital_logo" className="cursor-pointer">
                                  <div className="flex flex-col items-center space-y-2">
                                    <Image className="h-8 w-8 text-gray-400" />
                                    <div className="text-sm text-gray-300">
                                      <span className="text-blue-400 hover:text-blue-300">Click to upload</span> or drag and drop
                                    </div>
                                    <p className="text-xs text-gray-400">JPG, PNG, GIF or WebP (MAX. 5MB)</p>
                                  </div>
                                </label>
                                <input
                                  id="edit_hospital_logo"
                                  name="hospital_logo"
                                  type="file"
                                  accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                                  onChange={handleLogoChange}
                                  className="hidden"
                                />
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Divider */}
                    <div className="border-t border-gray-700 my-6"></div>

                    {/* Administrator Account Section */}
                    <div className="space-y-4">
                      <div className="flex items-center space-x-2 mb-4">
                        <User className="h-5 w-5 text-blue-400" />
                        <h3 className="text-lg font-semibold text-white">Administrator Account</h3>
                      </div>
                      
                      <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4 mb-4">
                        <p className="text-sm text-gray-300">
                          <strong className="text-white">Note:</strong> Leave password fields empty to keep the current password. 
                          Fill in both fields to reset the administrator password.
                        </p>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="edit_admin_password" className="text-white">
                            New Password
                          </Label>
                          <div className="relative">
                            <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                            <Input
                              id="edit_admin_password"
                              name="admin_password"
                              type={showPassword ? "text" : "password"}
                              placeholder="Leave empty to keep current"
                              value={editFormData.admin_password}
                              onChange={(e) => setEditFormData({ ...editFormData, admin_password: e.target.value })}
                              className="pl-10 pr-10 bg-gray-800 border-gray-600 text-white"
                            />
                            <button
                              type="button"
                              onClick={() => setShowPassword(!showPassword)}
                              className="absolute right-3 top-3 h-4 w-4 text-gray-400 hover:text-gray-300 focus:outline-none"
                              aria-label={showPassword ? "Hide password" : "Show password"}
                            >
                              {showPassword ? (
                                <EyeOff className="h-4 w-4" />
                              ) : (
                                <Eye className="h-4 w-4" />
                              )}
                            </button>
                          </div>
                          <p className="text-xs text-gray-400">Minimum 6 characters</p>
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="edit_confirm_password" className="text-white">
                            Confirm Password
                          </Label>
                          <div className="relative">
                            <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                            <Input
                              id="edit_confirm_password"
                              name="confirmPassword"
                              type={showConfirmPassword ? "text" : "password"}
                              placeholder="Confirm new password"
                              value={editFormData.confirmPassword}
                              onChange={(e) => setEditFormData({ ...editFormData, confirmPassword: e.target.value })}
                              className="pl-10 pr-10 bg-gray-800 border-gray-600 text-white"
                            />
                            <button
                              type="button"
                              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                              className="absolute right-3 top-3 h-4 w-4 text-gray-400 hover:text-gray-300 focus:outline-none"
                              aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                            >
                              {showConfirmPassword ? (
                                <EyeOff className="h-4 w-4" />
                              ) : (
                                <Eye className="h-4 w-4" />
                              )}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="flex gap-2 pt-4">
                      <Button
                        type="submit"
                        className="flex-1 medical-gradient text-white hover:opacity-90"
                      >
                        Update Hospital
                      </Button>
                      <Button
                        type="button"
                        onClick={() => setShowEditModal(false)}
                        variant="outline"
                        className="flex-1 border-gray-600 text-gray-300 hover:bg-gray-700"
                      >
                        Cancel
                      </Button>
                    </div>
                  </form>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        )}

        {/* Edit Plan Modal */}
        {showEditPlanModal && selectedPlanHospital && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="w-full max-w-2xl my-8"
            >
              <Card className="glass-effect border-gray-700">
                <CardHeader className="text-center p-6">
                  <div className="flex items-center justify-center space-x-2 mb-4">
                    <Shield className="h-8 w-8 text-blue-400" />
                    <span className="text-2xl font-bold gradient-text">Edit Hospital Plan</span>
                  </div>
                  <CardTitle className="text-2xl text-white">Update Subscription Plan</CardTitle>
                  <CardDescription className="text-gray-300">
                    {selectedPlanHospital.name}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={(e) => { e.preventDefault(); handleUpdatePlan(); }} className="space-y-6">
                    {/* Plan Type Selection */}
                    <div className="space-y-2">
                      <Label htmlFor="plan_type" className="text-white">
                        Plan Type <span className="text-red-400">*</span>
                      </Label>
                      <Select 
                        value={planFormData.plan_type} 
                        onValueChange={handlePlanTypeChange}
                      >
                        <SelectTrigger className="bg-gray-800 border-gray-600 text-white">
                          <SelectValue placeholder="Select plan type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="TRIAL">Trial</SelectItem>
                          <SelectItem value="SILVER">Silver</SelectItem>
                          <SelectItem value="GOLD">Gold</SelectItem>
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-gray-400">
                        {planFormData.plan_type === 'TRIAL' && 'Free trial period for new hospitals'}
                        {planFormData.plan_type === 'SILVER' && 'Silver plan with standard features'}
                        {planFormData.plan_type === 'GOLD' && 'Gold plan with premium features'}
                      </p>
                    </div>

                    {/* Billing Period (only for Silver/Gold) */}
                    {planFormData.plan_type !== 'TRIAL' && (
                      <div className="space-y-2">
                        <Label htmlFor="billing_period" className="text-white">
                          Billing Period <span className="text-red-400">*</span>
                        </Label>
                        <Select 
                          value={planFormData.billing_period} 
                          onValueChange={handleBillingPeriodChange}
                        >
                          <SelectTrigger className="bg-gray-800 border-gray-600 text-white">
                            <SelectValue placeholder="Select billing period" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="MONTHLY">Monthly</SelectItem>
                            <SelectItem value="YEARLY">Yearly</SelectItem>
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-gray-400">
                          {planFormData.billing_period === 'MONTHLY' && 'Billed monthly'}
                          {planFormData.billing_period === 'YEARLY' && 'Billed yearly (discounted rate)'}
                        </p>
                      </div>
                    )}

                    {/* Start Date */}
                    <div className="space-y-2">
                      <Label htmlFor="start_date" className="text-white">
                        Start Date <span className="text-red-400">*</span>
                      </Label>
                      <div className="relative">
                        <Calendar className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                        <Input
                          id="start_date"
                          type="date"
                          value={planFormData.start_date}
                          onChange={(e) => handleStartDateChange(e.target.value)}
                          className="pl-10 bg-gray-800 border-gray-600 text-white"
                          required
                        />
                      </div>
                    </div>

                    {/* End Date */}
                    <div className="space-y-2">
                      <Label htmlFor="end_date" className="text-white">
                        End Date <span className="text-red-400">*</span>
                      </Label>
                      <div className="relative">
                        <Calendar className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                        <Input
                          id="end_date"
                          type="date"
                          value={planFormData.end_date}
                          onChange={(e) => setPlanFormData({ ...planFormData, end_date: e.target.value })}
                          className="pl-10 bg-gray-800 border-gray-600 text-white"
                          required
                          min={planFormData.start_date}
                        />
                      </div>
                      <p className="text-xs text-gray-400">
                        {planFormData.plan_type !== 'TRIAL' && planFormData.billing_period && planFormData.start_date && (
                          <span className="text-blue-400">
                            Auto-calculated based on {planFormData.billing_period === 'MONTHLY' ? 'monthly' : 'yearly'} billing period
                          </span>
                        )}
                      </p>
                    </div>

                    {/* Info Box */}
                    <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                      <p className="text-sm text-gray-300">
                        <strong className="text-white">Note:</strong> Updating the plan will cancel any existing active subscriptions and create a new subscription with the selected plan and dates.
                      </p>
                    </div>

                    <div className="flex gap-2 pt-4">
                      <Button
                        type="submit"
                        className="flex-1 medical-gradient text-white hover:opacity-90"
                      >
                        Update Plan
                      </Button>
                      <Button
                        type="button"
                        onClick={() => {
                          setShowEditPlanModal(false);
                          setSelectedPlanHospital(null);
                        }}
                        variant="outline"
                        className="flex-1 border-gray-600 text-gray-300 hover:bg-gray-700"
                      >
                        Cancel
                      </Button>
                    </div>
                  </form>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        )}
      </div>
    </>
  );
};

export default DentalHubAdminDashboard;

